import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventsSliceState } from 'src/redesign/features/Events/types';
import { FEATURE_NAME } from 'src/redesign/features/Events/store/constants';
import {
  copyEvent,
  createAIEvent,
  createDateWithTimesEventOccurrence,
  createEvent,
  createEventOccurrence,
  createEventPromos,
  createEventTickets,
  createPreapprovedEvent,
  deleteDateWithTimesEventOccurrence,
  deleteEvent,
  deleteEventPromo,
  deleteEventTickets,
  fetchEventById,
  fetchEventByIdWithoutLoading,
  fetchEventLeafOccurrences,
  fetchEventOccurrences,
  fetchEventPromos,
  fetchEvents,
  fetchMoreEvents,
  fetchEventsBySite,
  fetchEventTickets,
  getEventTicketsWithoutMetrics,
  submitPreapprovedEvent,
  updateDateWithTimesEventOccurrence,
  updateEvent,
  updateEventOccurrence,
  updateEventPromos,
  updateEventTickets,
  updateEventTicketsOrder,
  updateEventWaitlist,
  fetchEventHosts,
  createEventHost,
  updateEventHost,
  deleteEventHost,
  inviteTeamMemberAsHost,
  addEventHostDirect,
} from 'src/redesign/features/Events/store/actions';
import { eventResponseTransform } from 'src/redesign/features/Events/utils/eventResponseTransform';
import { Event, EventResponse } from 'src/types/types';

const initialState: EventsSliceState = {
  currentEvent: null,
  currentEventError: null,
  loadingCurrentEvent: false,
  savingCurrentEvent: false,
  loadingTickets: false,
  tickets: [],
  events: [],
  siteEvents: [],
  loading: false,
  error: null,
  occurrences: null,
  promos: [],
  searchText: '',
  isLoadingOccurrences: false,
  eventAttendeesFilterValues: {},
  attendees: [],
  eventAttendeeStats: null,
  eventHosts: {},
  loadingEventHosts: false,
};

export const eventsSlice = createSlice({
  name: FEATURE_NAME,
  initialState,
  reducers: {
    handleChangeSearch: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setSavingCurrentEvent: (state, action: PayloadAction<boolean>) => {
      state.savingCurrentEvent = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Events
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      const eventsSource = action.payload?.data?.events ?? action.payload?.data ?? [];

      state.events = eventsSource.reduce((accum: Event[], item: EventResponse) => {
        accum.push(eventResponseTransform(item));
        return accum;
      }, [] as Event[]);
      state.error = null;
      state.loading = false;
    });
    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
    builder.addCase(fetchEvents.pending, (state) => {
      state.error = null;
      state.loading = true;
    });

    // Fetch More Events - does NOT set loading state (used for pagination)
    builder.addCase(fetchMoreEvents.fulfilled, (state, action) => {
      // Don't update state.events - pagination is handled in component local state
      state.error = null;
    });
    builder.addCase(fetchMoreEvents.rejected, (state, action) => {
      state.error = action.error.message;
    });
    // No pending case - we don't want to trigger global loading state

    // Fetch Events By Site
    builder.addCase(fetchEventsBySite.fulfilled, (state, action) => {
      state.siteEvents = action.payload.data.reduce((accum: Event[], item: EventResponse) => {
        accum.push(eventResponseTransform(item));
        return accum;
      }, [] as Event[]);
      state.error = null;
      state.loading = false;
    });
    builder.addCase(fetchEventsBySite.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
    builder.addCase(fetchEventsBySite.pending, (state) => {
      state.error = null;
      state.loading = true;
    });

    // Fetch Event By ID
    builder.addCase(fetchEventById.fulfilled, (state, action) => {
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.currentEventError = null;
      state.loadingCurrentEvent = false;
    });
    builder.addCase(fetchEventById.pending, (state) => {
      state.loadingCurrentEvent = true;
      state.currentEventError = null;
    });
    builder.addCase(fetchEventById.rejected, (state, action) => {
      state.loadingCurrentEvent = false;
      state.currentEventError = action.error.message;
    });
    builder.addCase(fetchEventByIdWithoutLoading.fulfilled, (state, action) => {
      state.currentEvent = eventResponseTransform(action.payload.data);
    });

    // Create Event
    builder.addCase(createEvent.fulfilled, (state, action) => {
      if (state.events) {
        state.events = [...state.events, eventResponseTransform(action.payload.data)];
      }
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.loading = false;
      state.savingCurrentEvent = false;
    });
    builder.addCase(createEvent.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createEvent.rejected, (state) => {
      state.loading = false;
      state.savingCurrentEvent = false;
    });

    // Create AI Event
    builder.addCase(createAIEvent.fulfilled, (state, action) => {
      if (state.events) {
        state.events = [...state.events, eventResponseTransform(action.payload.data)];
      }
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.loading = false;
    });

    // Update Event
    builder.addCase(updateEvent.fulfilled, (state, action) => {
      state.loading = false;
      state.loadingCurrentEvent = false;
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.savingCurrentEvent = false;
    });
    builder.addCase(updateEvent.pending, (state, action) => {
      state.loading = true;

      // If operation is "save", set savingCurrentEvent to true
      if (
        action.meta?.arg?.destination === 'save' ||
        action.meta?.arg?.destination === 'publish' ||
        action.meta?.arg?.destination === 'shopify'
      ) {
        state.savingCurrentEvent = true;
      } else {
        // Default to publish operation
        state.loadingCurrentEvent = true;
      }
    });

    // Copy Event
    builder.addCase(copyEvent.fulfilled, (state, action) => {
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.loading = false;
    });

    // Delete Event
    builder.addCase(deleteEvent.fulfilled, (state, action) => {
      if (state.events) {
        state.events = state.events.filter((event) => event.id !== action.meta.arg);
      }
      state.loading = false;
    });
    builder.addCase(deleteEvent.pending, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(deleteEvent.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    // Event Tickets
    builder.addCase(getEventTicketsWithoutMetrics.fulfilled, (state, action) => {
      state.tickets = action.payload.data;
      state.loadingTickets = false;
    });
    builder.addCase(getEventTicketsWithoutMetrics.pending, (state) => {
      state.loadingTickets = true;
    });
    builder.addCase(fetchEventTickets.fulfilled, (state, action) => {
      state.tickets = action.payload.data;
      state.loadingTickets = false;
    });
    builder.addCase(fetchEventTickets.pending, (state) => {
      state.loadingTickets = true;
    });
    builder.addCase(createEventTickets.fulfilled, (state, action) => {
      if (state.tickets) {
        state.tickets = [...state.tickets, action.payload.data];
      } else {
        state.tickets = [action.payload.data];
      }
    });
    builder.addCase(updateEventTickets.fulfilled, (state, action) => {
      if (state.tickets) {
        state.tickets = state.tickets.map((ticket) =>
          ticket.id === action.payload.data.id ? action.payload.data : ticket,
        );
      }
    });
    builder.addCase(updateEventTicketsOrder.pending, (state, action) => {
      const newOrder = action.meta.arg;
      if (state.tickets) {
        state.tickets = newOrder.map((ticket) => {
          const ticketData = state.tickets.find(({ id }) => id === ticket.id);
          return { ...ticketData, order: ticket.order };
        });
      }
    });
    builder.addCase(deleteEventTickets.fulfilled, (state, action) => {
      if (state.tickets) {
        state.tickets = state.tickets.filter((ticket) => ticket.id !== action.meta.arg);
      }
    });

    // Event Occurrences
    builder.addCase(fetchEventLeafOccurrences.fulfilled, (state, action) => {
      state.occurrences = action.payload.data;
      state.isLoadingOccurrences = false;
    });
    builder.addCase(fetchEventLeafOccurrences.pending, (state) => {
      state.isLoadingOccurrences = true;
    });
    builder.addCase(fetchEventOccurrences.fulfilled, (state, action) => {
      if (state.currentEvent) {
        state.currentEvent = { ...state.currentEvent, occurrences: action.payload.data };
      }
    });
    builder.addCase(createEventOccurrence.fulfilled, (state, action) => {
      if (state.currentEvent && state.currentEvent.occurrences) {
        state.currentEvent = {
          ...state.currentEvent,
          occurrences: [...state.currentEvent.occurrences, action.payload.data],
        };
      }
    });
    builder.addCase(updateEventOccurrence.fulfilled, (state, action) => {
      if (state.currentEvent && state.currentEvent.occurrences) {
        const occurrences = state.currentEvent.occurrences.map((occurrence: any) =>
          action.meta.arg.id === occurrence.id ? action.payload.data : occurrence,
        );
        state.currentEvent = { ...state.currentEvent, occurrences };
      }
    });
    builder.addCase(createDateWithTimesEventOccurrence.fulfilled, (state, action) => {
      if (state.currentEvent && state.currentEvent.occurrences) {
        state.currentEvent = {
          ...state.currentEvent,
          occurrences: [...state.currentEvent.occurrences, action.payload.data],
        };
      }
    });
    builder.addCase(updateDateWithTimesEventOccurrence.fulfilled, (state, action) => {
      if (state.currentEvent && state.currentEvent.occurrences) {
        const occurrences = state.currentEvent.occurrences.map((occurrence: any) =>
          action.meta.arg.id === occurrence.id ? action.payload.data : occurrence,
        );
        state.currentEvent = { ...state.currentEvent, occurrences };
      }
    });
    builder.addCase(deleteDateWithTimesEventOccurrence.fulfilled, (state, action) => {
      if (state.currentEvent && state.currentEvent.occurrences) {
        const occurrences = state.currentEvent.occurrences.filter(
          (occurrence: any) => occurrence.id !== action.meta.arg.id,
        );
        state.currentEvent = { ...state.currentEvent, occurrences };
      }
    });

    // Event Promos
    builder.addCase(fetchEventPromos.fulfilled, (state, action) => {
      state.promos = action.payload.data;
    });
    builder.addCase(createEventPromos.fulfilled, (state, action) => {
      if (state.promos) {
        state.promos = [...state.promos, action.payload.data];
      } else {
        state.promos = [action.payload.data];
      }
    });
    builder.addCase(updateEventPromos.fulfilled, (state, action) => {
      if (state.promos) {
        state.promos = state.promos.map((promo) =>
          action.meta.arg.id === promo.id ? { ...promo, ...action.meta.arg } : promo,
        );
      }
    });
    builder.addCase(deleteEventPromo.fulfilled, (state, action) => {
      if (state.promos) {
        state.promos = state.promos.filter((promo) => action.meta.arg.id !== promo.id);
      }
    });

    // Event Waitlist
    builder.addCase(updateEventWaitlist.fulfilled, (state, action) => {
      if (state.currentEvent) {
        state.currentEvent = { ...state.currentEvent, ...action.payload.data };
      }
    });
    // builder.addCase(updateEvent.fulfilled, (state, action) => {
    //   state.currentEvent = action.payload.data;
    //   state.error = null;
    //   state.loading = false;
    // });
    builder.addCase(updateEvent.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
      state.loadingCurrentEvent = false;
      state.savingCurrentEvent = false;
    });
    // builder.addCase(updateEvent.pending, (state) => {
    //   state.error = null;
    //   state.loading = true;
    // });
    builder.addCase(submitPreapprovedEvent.fulfilled, (state, action) => {
      state.currentEvent = eventResponseTransform(action.payload.data);
      state.savingCurrentEvent = false;
      state.error = null;
      state.loading = false;
    });
    builder.addCase(submitPreapprovedEvent.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
      state.savingCurrentEvent = false;
    });
    builder.addCase(submitPreapprovedEvent.pending, (state) => {
      state.error = null;
      state.loading = true;
      state.savingCurrentEvent = true;
    });
    builder.addCase(createPreapprovedEvent.fulfilled, (state, action) => {
      state.savingCurrentEvent = false;
      state.error = null;
      state.loading = false;
    });
    builder.addCase(createPreapprovedEvent.rejected, (state, action) => {
      state.loading = false;
      state.savingCurrentEvent = false;
    });
    builder.addCase(createPreapprovedEvent.pending, (state) => {
      state.loading = true;
      state.savingCurrentEvent = true;
    });

    // Fetch Event Hosts
    builder.addCase(fetchEventHosts.pending, (state) => {
      state.loadingEventHosts = true;
    });
    builder.addCase(fetchEventHosts.fulfilled, (state, action) => {
      const eventId = action.meta.arg;
      state.eventHosts[eventId] = action.payload.data;
      state.loadingEventHosts = false;
    });
    builder.addCase(fetchEventHosts.rejected, (state) => {
      state.loadingEventHosts = false;
    });

    // Update Event Host
    builder.addCase(updateEventHost.fulfilled, (state, action) => {
      const { eventId } = action.meta.arg;
      const updatedHost = action.payload.data;

      if (state.eventHosts[eventId]) {
        const index = state.eventHosts[eventId].findIndex((h) => h.id === updatedHost.id);
        if (index !== -1) {
          state.eventHosts[eventId][index] = updatedHost;
        }
      }
    });

    // Create Event Host
    builder.addCase(createEventHost.fulfilled, (state, action) => {
      const { eventId } = action.meta.arg;
      const newHost = action.payload.data;

      if (!state.eventHosts[eventId]) {
        state.eventHosts[eventId] = [];
      }
      state.eventHosts[eventId].push(newHost);
    });

    // Delete Event Host
    builder.addCase(deleteEventHost.fulfilled, (state, action) => {
      const { eventId, memberId } = action.meta.arg;

      if (state.eventHosts[eventId]) {
        state.eventHosts[eventId] = state.eventHosts[eventId].filter(
          (host) => host.team_member_id !== memberId,
        );
      }
    });

    // Invite Team Member As Host
    builder.addCase(inviteTeamMemberAsHost.fulfilled, (state, action) => {
      const { eventId } = action.meta.arg;
      const eventHost = action.payload.data.event_host;

      if (eventHost && state.eventHosts[eventId]) {
        state.eventHosts[eventId].push(eventHost);
      }
    });

    // Add Event Host Direct
    builder.addCase(addEventHostDirect.fulfilled, (state, action) => {
      const { eventId } = action.meta.arg;
      const newHost = action.payload.data;

      if (!state.eventHosts[eventId]) {
        state.eventHosts[eventId] = [];
      }
      state.eventHosts[eventId].push(newHost);
    });
  },
});

export const { setSavingCurrentEvent } = eventsSlice.actions;
